import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useAppConfig } from '../../contexts/AppConfigContext';
import './GamePlay.css';

const TEMPLATE_GAME_IDS = ['QUIZ_RUSH', 'MEMORY_MATCH', 'WORD_BUILDER', 'FORMULA_RACE', 'CHAIN_REACTION'];
const TEMPLATE_NAMES = { QUIZ_RUSH: 'Quiz Rush', MEMORY_MATCH: 'Memory Match', WORD_BUILDER: 'Word Builder', FORMULA_RACE: 'Formula Race', CHAIN_REACTION: 'Chain Reaction' };
const SUBJECTS = ['Mathematics', 'English', 'Physics', 'Chemistry', 'Biology'];

const GamePlay = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { appName } = useAppConfig();
  const isTemplate = TEMPLATE_GAME_IDS.includes(gameId);
  const [templateConfig, setTemplateConfig] = useState({ subject: 'Mathematics', difficulty: 'medium' });
  const [showTemplateConfig, setShowTemplateConfig] = useState(false);
  const [gameSession, setGameSession] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [showHint, setShowHint] = useState(false);

  const startGame = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (isTemplate) {
        const res = await api.post('/games/start', {
          gameType: gameId,
          subject: templateConfig.subject,
          difficulty: templateConfig.difficulty
        });
        if (!res.data.success || !res.data.data?.session) {
          setError(res.data?.message || 'Failed to start game');
          return;
        }
        const session = res.data.data.session;
        const questions = session.questions || [];
        const pointsPerQ = session.config?.pointsPerCorrect || 100;
        const mappedSession = {
          sessionId: session.id,
          game: {
            title: session.config?.name || TEMPLATE_NAMES[gameId] || gameId,
            questions: questions.map((q) => ({
              id: q.id,
              question: q.question,
              options: Array.isArray(q.options)
                ? q.options.map((opt, i) => (typeof opt === 'object' && opt && 'text' in opt ? opt : { id: opt?.id ?? String(i), text: typeof opt === 'string' ? opt : opt?.text ?? String(opt) }))
                : [],
              correctAnswer: q.correctAnswer,
              points: q.points ?? pointsPerQ,
              type: 'multiple_choice'
            })),
            config: { timeLimit: session.config?.timeLimit }
          },
          progress: {
            score: { current: 0, maximum: questions.length * pointsPerQ },
            questions: { correct: 0 },
            timeSpent: { total: 0 }
          }
        };
        setGameSession(mappedSession);
        if (session.config?.timeLimit) setTimeLeft(session.config.timeLimit);
        setShowTemplateConfig(false);
      } else {
        const response = await api.post(`/games/custom/${gameId}/start`);
        if (response.data.success) {
          const session = response.data.data;
          setGameSession(session);
          if (session.game?.config?.timeLimit) setTimeLeft(session.game.config.timeLimit);
        }
      }
      setQuestionStartTime(Date.now());
    } catch (err) {
      console.error('Error starting game:', err);
      setError(err.response?.data?.message || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  }, [gameId, isTemplate, templateConfig]);

  useEffect(() => {
    if (isTemplate) {
      setShowTemplateConfig(true);
      setLoading(false);
    } else {
      startGame();
    }
  }, [gameId, isTemplate]);

  const startTemplateWithConfig = () => {
    setShowTemplateConfig(false);
    setLoading(true);
    startGame();
  };

  useEffect(() => {
    startGame();
  }, [startGame]);

  const getCurrentQuestion = useCallback(() => {
    return gameSession?.game.questions[currentQuestionIndex];
  }, [gameSession, currentQuestionIndex]);

  

  const submitAnswer = useCallback(async (answer = selectedAnswer, isTimeUp = false) => {
    if (submitting || gameCompleted || !gameSession) return;
    const q = getCurrentQuestion();
    if (!q || q.id == null) {
      setError('Your game session may have expired. Please go back and start a new game.');
      return;
    }
    try {
      setSubmitting(true);
      const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
      if (isTemplate) {
        const response = await api.post('/games/answer', {
          sessionId: gameSession.sessionId,
          questionId: q.id,
          answer: answer || '',
          timeSpent
        });
        if (response.data.success) {
          const d = response.data.data;
          const newScore = (gameSession.progress?.score?.current ?? 0) + (d.points ?? 0);
          const newCorrect = (gameSession.progress?.questions?.correct ?? 0) + (d.isCorrect ? 1 : 0);
          setLastResult({
            isCorrect: d.isCorrect,
            points: d.points ?? 0,
            correctAnswer: d.correctAnswer,
            explanation: d.explanation,
            progress: {
              score: { current: newScore, maximum: gameSession.progress?.score?.maximum ?? 0 },
              questions: { correct: newCorrect }
            }
          });
          setShowResult(true);
          setGameSession(prev => ({
            ...prev,
            progress: {
              ...prev.progress,
              score: { ...prev.progress?.score, current: newScore },
              questions: { ...prev.progress?.questions, correct: newCorrect }
            }
          }));
          const nextIdx = currentQuestionIndex + 1;
          if (nextIdx >= (gameSession.game?.questions?.length ?? 0)) setGameCompleted(true);
        }
      } else {
        const response = await api.post(`/games/custom/${gameId}/answer`, {
          sessionId: gameSession.sessionId,
          questionId: q.id,
          answer: answer || '',
          timeSpent: timeSpent,
          hintsUsed: hintsUsed
        });
        if (response.data.success) {
          const result = response.data.data;
          setLastResult(result);
          setShowResult(true);
          setGameSession(prev => ({ ...prev, progress: result.progress }));
          if (result.progress?.status === 'completed') setGameCompleted(true);
        }
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      const msg = err.response?.data?.message || 'Failed to submit answer';
      const isValidationFailed = /validation failed/i.test(msg);
      setError(isValidationFailed ? 'Your session may have expired or something went wrong. Please go back and start a new game.' : msg);
    } finally {
      setSubmitting(false);
    }
  }, [selectedAnswer, submitting, gameCompleted, questionStartTime, gameId, gameSession, currentQuestionIndex, isTemplate, hintsUsed, getCurrentQuestion]);

  const handleTimeUp = useCallback(() => {
    if (!submitting && !gameCompleted) {
      submitAnswer('', true); // Submit empty answer when time is up
    }
  }, [submitting, gameCompleted, submitAnswer]);

  useEffect(() => {
    if (gameSession && gameSession.game.config.timeLimit && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, gameSession, handleTimeUp]);


  const nextQuestion = () => {
    setShowResult(false);
    setSelectedAnswer('');
    setHintsUsed(0);
    setShowHint(false);
    
    if (gameCompleted) {
      // Show final results
      return;
    }
    
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < gameSession.game.questions.length) {
      setCurrentQuestionIndex(nextIndex);
      setQuestionStartTime(Date.now());
      
      // Reset timer for next question
      if (gameSession.game.config.timeLimit) {
        setTimeLeft(gameSession.game.config.timeLimit);
      }
    }
  };

  const useHint = () => {
    setHintsUsed(prev => prev + 1);
    setShowHint(true);
  };

  

  const getProgressPercentage = () => {
    if (!gameSession) return 0;
    return Math.round((currentQuestionIndex / gameSession.game.questions.length) * 100);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const restartGame = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer('');
    setTimeLeft(0);
    setGameCompleted(false);
    setShowResult(false);
    setLastResult(null);
    setHintsUsed(0);
    setShowHint(false);
    startGame();
  };

  const exitGame = () => {
    navigate('/games');
  };

  const GameBrandBar = ({ showExit = true, exitLabel = 'Back to games' }) => (
    <div className="game-brand-bar">
      <button type="button" className="game-brand-link" onClick={() => navigate('/games')} aria-label="Games home">
        <span className="game-brand-name">{appName}</span>
        <span className="game-brand-sep">·</span>
        <span className="game-brand-games">Games</span>
      </button>
      {showExit && (
        <button type="button" className="game-brand-exit" onClick={exitGame}>{exitLabel}</button>
      )}
    </div>
  );

  if (isTemplate && showTemplateConfig) {
    return (
      <div className="game-play game-play--setup">
        <GameBrandBar />
        <div className="game-setup-card">
          <span className="game-message-card__brand">{appName}</span>
          <h2 className="game-setup-card__title">{TEMPLATE_NAMES[gameId] || gameId}</h2>
          <p className="game-setup-card__subtitle">Choose subject and difficulty to start.</p>
          <div className="game-setup-card__field">
            <label className="game-setup-card__label">Subject</label>
            <select value={templateConfig.subject} onChange={(e) => setTemplateConfig((c) => ({ ...c, subject: e.target.value }))} className="game-setup-card__input">
              {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="game-setup-card__field">
            <label className="game-setup-card__label">Difficulty</label>
            <select value={templateConfig.difficulty} onChange={(e) => setTemplateConfig((c) => ({ ...c, difficulty: e.target.value }))} className="game-setup-card__input">
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <button type="button" onClick={startTemplateWithConfig} className="game-btn game-btn--primary">Start game</button>
          <button type="button" onClick={() => navigate('/games')} className="game-btn game-btn--secondary">Back to games</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="game-play">
        <GameBrandBar showExit={false} />
        <div className="game-loading">
          <div className="game-loading-spinner" aria-hidden />
          <p className="game-loading-text">Starting game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isNoQuestions = /no questions available/i.test(error);
    const isSessionExpired = /session may have expired|validation failed/i.test(error);
    const title = isNoQuestions ? 'No questions yet' : isSessionExpired ? 'Session expired' : 'Something went wrong';
    return (
      <div className="game-play">
        <GameBrandBar />
        <div className="game-message-card game-message-card--error">
          <span className="game-message-card__brand">{appName}</span>
          <h1 className="game-message-card__title">{title}</h1>
          <p className="game-message-card__body">{error}</p>
          <div className="game-message-card__actions">
            {isTemplate && isNoQuestions ? (
              <button type="button" onClick={() => { setError(null); setShowTemplateConfig(true); setLoading(false); }} className="game-btn game-btn--primary">
                Try different subject or difficulty
              </button>
            ) : (
              <button type="button" onClick={startGame} className="game-btn game-btn--primary">Try again</button>
            )}
            <button type="button" onClick={exitGame} className="game-btn game-btn--secondary">Back to games</button>
          </div>
        </div>
      </div>
    );
  }

  if (!gameSession) {
    return (
      <div className="game-play">
        <GameBrandBar />
        <div className="game-message-card game-message-card--error">
          <span className="game-message-card__brand">{appName}</span>
          <h1 className="game-message-card__title">Game not found</h1>
          <p className="game-message-card__body">The requested game could not be loaded.</p>
          <div className="game-message-card__actions">
            <button type="button" onClick={exitGame} className="game-btn game-btn--primary">Back to games</button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = getCurrentQuestion();

  if (gameCompleted && showResult) {
    return (
      <div className="game-play">
        <GameBrandBar />
        <div className="game-completed">
          <div className="completion-header">
            <h2>🎉 Game Completed!</h2>
            <p>Congratulations on finishing {gameSession.game.title}</p>
          </div>
          
          <div className="final-stats">
            <div className="stat-card">
              <div className="stat-value">{gameSession.progress.score.current}</div>
              <div className="stat-label">Final Score</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{gameSession.progress.score.percentage}%</div>
              <div className="stat-label">Accuracy</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{gameSession.progress.questions.correct}</div>
              <div className="stat-label">Correct Answers</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{formatTime(gameSession.progress.timeSpent.total)}</div>
              <div className="stat-label">Time Spent</div>
            </div>
          </div>
          
          {gameSession.progress.achievements && gameSession.progress.achievements.length > 0 && (
            <div className="achievements">
              <h3>Achievements Unlocked</h3>
              <div className="achievement-list">
                {gameSession.progress.achievements.map((achievement, index) => (
                  <div key={index} className="achievement-item">
                    <span className="achievement-icon">🏆</span>
                    <span className="achievement-name">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="completion-actions">
            <button onClick={restartGame} className="restart-button">
              Play Again
            </button>
            <button onClick={exitGame} className="exit-button">
              Back to Games
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResult && lastResult) {
    return (
      <div className="game-play">
        <GameBrandBar />
        <div className="result-screen">
          <div className="result-header">
            <div className={`result-icon ${lastResult.isCorrect ? 'correct' : 'incorrect'}`}>
              {lastResult.isCorrect ? '✅' : '❌'}
            </div>
            <h2>{lastResult.isCorrect ? 'Correct!' : 'Incorrect'}</h2>
          </div>
          
          <div className="result-details">
            <div className="points-earned">
              <span className="points-label">Points Earned:</span>
              <span className="points-value">+{lastResult.points}</span>
            </div>
            
            {!lastResult.isCorrect && (
              <div className="correct-answer">
                <span className="answer-label">Correct Answer:</span>
                <span className="answer-value">{lastResult.correctAnswer}</span>
              </div>
            )}
            
            {lastResult.explanation && (
              <div className="explanation">
                <h4>Explanation:</h4>
                <p>{typeof lastResult.explanation === 'string' ? lastResult.explanation : lastResult.explanation?.text || 'No explanation available'}</p>
              </div>
            )}
          </div>
          
          <div className="progress-summary">
            <div className="score-display">
              Score: {lastResult.progress.score.current} / {lastResult.progress.score.maximum}
            </div>
            <div className="questions-progress">
              Question {currentQuestionIndex + 1} of {gameSession.game.questions.length}
            </div>
          </div>
          
          <div className="result-screen-actions">
            <button onClick={nextQuestion} className="next-button">
              {gameCompleted ? 'View Results' : 'Next Question'}
            </button>
            <button type="button" onClick={exitGame} className="game-header-exit result-exit">
              Exit game
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-play game-play--play">
      <GameBrandBar exitLabel="Exit" />
      <div className="game-play-content">
      <div className="game-header">
        <div className="game-info">
          <h1>{gameSession.game.title}</h1>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
          <div className="progress-text">
            Question {currentQuestionIndex + 1} of {gameSession.game.questions.length}
          </div>
        </div>
        
        <div className="game-stats">
          <div className="stat-item">
            <span className="stat-label">Score</span>
            <span className="stat-value">{gameSession.progress.score.current}</span>
          </div>
          {gameSession.game.config.timeLimit && (
            <div className="stat-item">
              <span className="stat-label">Time</span>
              <span className={`stat-value ${timeLeft <= 10 ? 'warning' : ''}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={exitGame}
            className="game-header-exit"
            aria-label="Exit game"
          >
            Exit
          </button>
        </div>
      </div>

      <div className="question-container">
        <div className="question-header">
          <h2 className="question-text">{currentQuestion.question?.text || currentQuestion.question?.question || (typeof currentQuestion.question === 'string' ? currentQuestion.question : 'Question text not available')}</h2>
          
          {currentQuestion.hint && (
            <div className="hint-section">
              {!showHint ? (
                <button onClick={useHint} className="hint-button">
                  💡 Use Hint (-2 points)
                </button>
              ) : (
                <div className="hint-display">
                  <span className="hint-icon">💡</span>
                  <span className="hint-text">{currentQuestion.hint}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="question-content">
          {currentQuestion.type === 'multiple_choice' && (
            <div className="options-container">
              {currentQuestion.options.map((option) => (
                <button
                  key={option.id}
                  className={`option-button ${
                    selectedAnswer === option.id ? 'selected' : ''
                  }`}
                  onClick={() => setSelectedAnswer(option.id)}
                  disabled={submitting}
                >
                  <span className="option-letter">
                    {String.fromCharCode(65 + currentQuestion.options.indexOf(option))}
                  </span>
                  <span className="option-text">{option.text}</span>
                </button>
              ))}
            </div>
          )}

          {currentQuestion.type === 'true_false' && (
            <div className="true-false-container">
              <button
                className={`tf-button ${selectedAnswer === 'true' ? 'selected' : ''}`}
                onClick={() => setSelectedAnswer('true')}
                disabled={submitting}
              >
                True
              </button>
              <button
                className={`tf-button ${selectedAnswer === 'false' ? 'selected' : ''}`}
                onClick={() => setSelectedAnswer('false')}
                disabled={submitting}
              >
                False
              </button>
            </div>
          )}

          {currentQuestion.type === 'fill_blank' && (
            <div className="fill-blank-container">
              <input
                type="text"
                value={selectedAnswer}
                onChange={(e) => setSelectedAnswer(e.target.value)}
                placeholder="Type your answer here..."
                className="fill-blank-input"
                disabled={submitting}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && selectedAnswer.trim()) {
                    submitAnswer();
                  }
                }}
              />
            </div>
          )}
        </div>

        <div className="question-actions">
          <button
            onClick={() => submitAnswer()}
            disabled={!selectedAnswer || submitting}
            className="submit-button"
          >
            {submitting ? 'Submitting...' : 'Submit Answer'}
          </button>
          
          <button onClick={exitGame} className="exit-game-button">
            Exit Game
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default GamePlay;